/**
 * NotificationBell — the AppShell header bell.
 *
 * Backed by the notification catalogue's `notifications` table, not
 * the old two-source aggregator (announcements' useAnnouncements + the
 * stored `work_notifications` 'assigned' rows via
 * useWorkNotifications). notify() raises `announcement_posted` for
 * every announcement (api/_shared/repos/announcements.ts) and
 * `case_assigned` / `task_assigned` / `card_handed_to_you` for every
 * assignment or hand-off (api/_shared/repos/workItems.ts) into this
 * SAME table, so those two old sources would otherwise surface the
 * same events twice. One bell, one substrate.
 *
 * KEPT: the "Overdue" section. There is no catalogue event for a work
 * item going overdue (NOTIFICATION_EVENT_KEYS has no `task_overdue`);
 * it is a LIVE COMPUTATION over due dates, never a stored record. It
 * still comes from GET /work-notifications (workNotificationsApi),
 * reading only its `overdue` array; the `notifications`/`unread_count`
 * fields from that same response are no longer read anywhere, and this
 * bell no longer calls its `/seen` mark either. The `work_notifications`
 * 'assigned' writes those fields used to feed are now unread by any UI;
 * that is a documented follow-up, not something this file changes.
 *
 * Server is authoritative on the unread count (polled every 60s +
 * refreshed on window focus) and on the effective preference a person
 * chose: an event switched off is written already read, so it sits in
 * the inbox history and never in this count or this panel.
 *
 * The panel is the unread queue: it asks the server for unread rows
 * only, newest first, and says so when there are none. The inbox page
 * (/notifications) is the history. The list stays live while the bell
 * is mounted and refetches whenever the panel opens, so a row a person
 * read, or marked all read, is gone the next time the panel opens.
 * Self-gates on view_staff_tools, the same floor the prior bell used.
 */

import { useState, useRef, useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { canViewStaffTools, canViewWorkspace } from '@/lib/capabilities';
import { useWorkNotifications } from '@/lib/workNotifications';
import {
  useNotificationsUnreadCount,
  useNotificationsList,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useRefetchUnreadCountOnFocus,
  notificationHref,
} from '@/lib/notifications';
import type { NotificationWire } from '@/lib/api';

const BELL_PANEL_LIMIT = 10;

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** 'YYYY-MM-DD' (or an ISO timestamp) -> 'Mon D' without timezone drift; '' for null/blank. */
function formatDate(value: string | null): string {
  if (!value) return '';
  const parts = value.slice(0, 10).split('-').map(Number);
  const month = parts[1];
  const day = parts[2];
  if (!month || !day || month < 1 || month > 12) return '';
  return `${MONTHS[month - 1]} ${day}`;
}

/** A rough relative time for the panel row ("just now", "2h ago", a date
 *  once it's more than a day old — the inbox page has exact timestamps). */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return formatDate(iso);
}

export function NotificationBell(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const navigate = useNavigate();
  const hasAccess = canViewStaffTools(whoami);
  const canOpenBoard = canViewWorkspace(whoami);

  const { unreadCount } = useNotificationsUnreadCount(tenantSlug, { enabled: hasAccess });
  useRefetchUnreadCountOnFocus(tenantSlug);

  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { rows, isLoading, isError } = useNotificationsList(tenantSlug, {
    unreadOnly: true,
    limit: BELL_PANEL_LIMIT,
    enabled: hasAccess,
  });
  const markRead = useMarkNotificationRead(tenantSlug);
  const markAllRead = useMarkAllNotificationsRead(tenantSlug);

  // The still-unique computed overdue list (see the header comment).
  const { overdue } = useWorkNotifications(tenantSlug, { enabled: hasAccess });

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!hasAccess) return null;

  function openRow(row: NotificationWire): void {
    if (!row.read_at) markRead.mutate(row.id);
    setOpen(false);
    navigate(notificationHref(tenantSlug, row.link));
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="notification-bell"
        onClick={() => {
          setOpen((v) => {
            // Opening refetches the count and the list together, so the
            // badge and the panel agree on what is unread.
            if (!v) void queryClient.invalidateQueries({ queryKey: ['notifications', tenantSlug] });
            return !v;
          });
        }}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="menu"
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-pill border border-sage-800/[8%] bg-paper text-sage-400 shadow-sh1 transition-all duration-[var(--dur-base)] ease-quiet motion-reduce:transition-none hover:text-sage-800 hover:border-sage-400/20 hover:shadow-sh2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      >
        <Bell size={19} aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-bell-badge"
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-pill bg-warm-400 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-paper"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Notifications"
          data-testid="notification-bell-dropdown"
          className="absolute right-0 top-12 w-80 max-h-[28rem] overflow-auto bg-paper rounded-2xl shadow-sh3 border border-sage-800/[8%] py-2 z-50"
        >
          {overdue.length > 0 && (
            <div data-testid="notification-bell-overdue">
              <div className="px-4 py-2 border-b border-sage-800/[8%]">
                <p className="text-sm font-semibold text-sage-800">Overdue</p>
              </div>
              {overdue.map((o) =>
                canOpenBoard ? (
                  <Link
                    key={`overdue-${o.id}`}
                    to={`/t/${tenantSlug}/workspace?view=mywork&item=${encodeURIComponent(o.id)}`}
                    onClick={() => setOpen(false)}
                    data-testid="bell-overdue-row"
                    className="block px-4 py-2.5 border-b border-sage-800/[6%] border-l-2 border-l-pepper hover:bg-sage-800/[4%] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                  >
                    <p className="text-[13px] text-ink">{o.title}</p>
                    <p className="mt-0.5 text-xs font-medium text-pepper">Overdue · {formatDate(o.due_date)}</p>
                  </Link>
                ) : (
                  <div
                    key={`overdue-${o.id}`}
                    data-testid="bell-overdue-row"
                    className="px-4 py-2.5 border-b border-sage-800/[6%] border-l-2 border-l-pepper"
                  >
                    <p className="text-[13px] text-ink">{o.title}</p>
                    <p className="mt-0.5 text-xs font-medium text-pepper">Overdue · {formatDate(o.due_date)}</p>
                  </div>
                ),
              )}
            </div>
          )}

          <div className="px-4 py-2 border-b border-sage-800/[8%] flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-sage-800">Notifications</p>
            {unreadCount > 0 && (
              <button
                type="button"
                data-testid="notification-bell-mark-all-read"
                onClick={() => markAllRead.mutate()}
                className="text-xs font-medium text-sage-600 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
              >
                Mark all read
              </button>
            )}
          </div>

          {isLoading ? (
            <p className="px-4 py-3 text-sm text-ink-soft">Loading…</p>
          ) : isError ? (
            <p data-testid="notification-bell-list-error" className="px-4 py-3 text-sm text-ink-soft">
              Couldn’t load notifications.
            </p>
          ) : rows.length === 0 ? (
            <p data-testid="notification-bell-caught-up" className="px-4 py-3 text-sm text-ink-soft">
              You’re all caught up.
            </p>
          ) : (
            rows.map((row) => (
              <button
                key={row.id}
                type="button"
                data-testid="notification-bell-row"
                onClick={() => openRow(row)}
                className={
                  'block w-full text-left px-4 py-2.5 border-b border-sage-800/[6%] last:border-b-0 hover:bg-sage-800/[4%] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                  (row.read_at ? '' : 'bg-warm-50')
                }
              >
                <p className="text-[13px] text-ink font-medium">{row.title}</p>
                <p className="mt-0.5 text-xs text-ink-soft">{row.summary}</p>
                <p className="mt-0.5 text-xs text-sage-600">{relativeTime(row.created_at)}</p>
              </button>
            ))
          )}

          <div className="px-4 py-2 flex items-center justify-between gap-2 border-t border-sage-800/[8%]">
            <Link
              to={`/t/${tenantSlug}/notifications`}
              data-testid="notification-bell-see-all"
              onClick={() => setOpen(false)}
              className="text-xs font-medium text-sage-600 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
            >
              See all
            </Link>
            <Link
              to={`/t/${tenantSlug}/settings/notifications`}
              data-testid="notification-bell-settings"
              onClick={() => setOpen(false)}
              className="text-xs font-medium text-sage-600 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded"
            >
              Notification settings
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
